import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { isEmpty } from "lodash";
import { FirstDayOfWeek, parseDay } from "../../lib/datetime";
import { useI18n } from "../../lib/i18n/i18n_provider";
import { DayPassDateRange } from "../../offering/order_params";
import { DatePickerV2, Interval } from "./date_picker_v2";
import { toDay } from "../../offering/day_utils";
import { Icon } from "../../components";
import { formatDayRange } from "../../offering/format_date_range";
import { useAnalytics } from "../../lib/analytics/analytics";
import {
  useBestDayRatesQuery,
  getViewRange,
} from "../hooks/use_best_day_rates_query";
import { BookingType, PricingType, Space } from "../../data";
import { usePriceDiscoverabilityFlag } from "../../core/splitio";
import { toBestDateRates } from "../utils/to_best_date_rates";
import {
  DateRangePicker,
  PickingRange,
} from "./date_range_picker/date_range_picker";
import { endOfMonth, startOfMonth } from "date-fns";

interface CheckoutDatePickerProps {
  value: DayPassDateRange | undefined;
  onComplete: (dateRange?: DayPassDateRange) => void;
  onNextVisibleDates: (startDate: Date, endDate: Date) => void;
  isOutsideRange: (date: Date) => boolean;
  isBlockedDate: (date: Date) => boolean;
  firstDayOfWeek?: FirstDayOfWeek;
  forceInitialSingleDate?: boolean;
  placeholder?: string;
  isDateRangeSelectEnabled?: boolean;
  availableDays?: Date[];
  disableRangeSelection: boolean;
  space?: Space | null;
}

export function CheckoutDatePicker(props: CheckoutDatePickerProps) {
  const {
    value,
    onComplete,
    forceInitialSingleDate,
    isOutsideRange,
    firstDayOfWeek,
    isBlockedDate,
    onNextVisibleDates,
    placeholder,
    isDateRangeSelectEnabled = true,
    availableDays,
    disableRangeSelection,
    space,
  } = props;
  const priceDiscoverabilityFlag = usePriceDiscoverabilityFlag(
    space?.partnerID || ""
  );

  const [internalValue, setInternalValue] = useState(value);

  const [show, setShow] = useState<boolean>(false);
  const [viewingRange, setViewingRange] = useState<{
    startDate: Date;
    endDate: Date;
  }>();
  const analytics = useAnalytics();
  const { data: bestDayRates } = useBestDayRatesQuery(
    getViewRange(viewingRange, internalValue),
    BookingType.Daily,
    space?.currencyCode,
    space?.id
  );

  const basePrice = space?.pricings.find(
    (p) => p.type === PricingType.Daily
  )?.price;

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  const handleChange = useCallback(
    (value: Interval | Date) => {
      if (value instanceof Date) {
        const date = toDay(value);

        setInternalValue({
          startDate: date,
          endDate: date,
        });

        onComplete({
          startDate: date,
          endDate: date,
        });

        analytics.event(
          "Date Select Click",
          {
            Date: date,
            Source: "WL",
          },
          { onlyProviders: ["mixpanel", "customerio"] }
        );
      } else {
        value = value as Interval;
        const startDate = toDay(value.start);
        const endDate = toDay(value.end);

        setInternalValue({
          startDate: startDate,
          endDate: endDate,
        });

        onComplete({
          startDate: startDate,
          endDate: endDate,
        });

        analytics.event(
          "Date Select Click",
          {
            Date: endDate,
            Source: "WL",
          },
          { onlyProviders: ["mixpanel", "customerio"] }
        );
      }
    },
    [analytics, onComplete]
  );

  const currentValue = useMemo(() => {
    if (isDateRangeSelectEnabled) {
      return internalValue?.startDate && internalValue?.endDate
        ? {
            start: parseDay(internalValue.startDate),
            end: parseDay(internalValue.endDate),
          }
        : undefined;
    } else {
      return internalValue?.startDate
        ? parseDay(internalValue?.startDate)
        : undefined;
    }
  }, [internalValue, isDateRangeSelectEnabled]);

  const dpCurrentValue = useMemo(() => {
    if (isDateRangeSelectEnabled) {
      return {
        startDate: internalValue?.startDate
          ? parseDay(internalValue.startDate)
          : undefined,
        endDate: internalValue?.endDate
          ? parseDay(internalValue.endDate)
          : undefined,
      };
    } else {
      const startDate = internalValue?.startDate
        ? parseDay(internalValue.startDate)
        : undefined;
      return {
        startDate: startDate,
        endDate: startDate,
      };
    }
  }, [internalValue, isDateRangeSelectEnabled]);

  const forceSingleDate =
    forceInitialSingleDate &&
    internalValue === value &&
    internalValue?.startDate === internalValue?.endDate;

  const { fbt, locale } = useI18n();

  const handleViewDates = useCallback(
    (startDate: Date, endDate: Date, viewingDate: Date) => {
      setViewingRange({
        startDate: startOfMonth(viewingDate),
        endDate: endOfMonth(viewingDate),
      });
      onNextVisibleDates(startDate, endDate);
    },
    [onNextVisibleDates]
  );

  const dynamicPricings = toBestDateRates(
    bestDayRates.options,
    undefined,
    space?.currencyCode
  );

  const saveButtonComponent = (
    <div style={{ padding: 16 }}>
      <button
        disabled={!currentValue}
        className="btn btn-primary"
        onClick={() => {
          setShow(false);

          analytics.event(
            "Save Date Click",
            {
              "Booking Start Date": internalValue?.startDate,
              "Booking End Date": internalValue?.startDate,
              Source: "WL",
            },
            { onlyProviders: ["mixpanel", "customerio"] }
          );
        }}
      >
        {fbt("Save")}
      </button>
    </div>
  );

  const dateRangePickerComponent = priceDiscoverabilityFlag ? (
    <>
      <DateRangePicker
        availableDates={availableDays}
        pickingRange={
          disableRangeSelection
            ? PickingRange.OneDate
            : PickingRange.Consecutive
        }
        basePrice={basePrice}
        onChange={({ startDate, endDate }) => {
          handleChange({
            start: startDate,
            end: endDate,
          });
        }}
        currency={space?.currencyCode || undefined}
        dynamicPricings={dynamicPricings}
        hidePrice={isEmpty(dynamicPricings)}
        value={dpCurrentValue}
        onViewDates={handleViewDates}
        partnerId={space?.partnerID}
      />
      {saveButtonComponent}
    </>
  ) : (
    <>
      <DatePickerV2
        firstDayOfWeek={firstDayOfWeek}
        isRange={isDateRangeSelectEnabled}
        onChange={handleChange}
        value={currentValue}
        isBlocked={isBlockedDate}
        isOutsideRange={isOutsideRange}
        numberOfMonths={1}
        forceSingleDate={forceSingleDate}
        onNextVisibleDates={onNextVisibleDates}
        orientation="horizontal"
      />
      {saveButtonComponent}
    </>
  );

  return (
    <div className="checkout-box-picker-view">
      <Dropdown
        className="checkout-box-picker row"
        id={`checkout-date-picker`}
        show={show}
        onToggle={(isOpen) => setShow(isOpen)}
        data-testid="checkout-date-picker"
      >
        <Dropdown.Toggle>
          <div className="row">
            <div className="col-10 checkout-box-picker-placeholder">
              <span className="row fw-bold text-uppercase checkout-box-picker-placeholder__label">
                {placeholder ? fbt(placeholder) : fbt("Date")}
              </span>
              <span className="row checkout-box-picker-selected">
                {value?.startDate && value?.endDate ? (
                  formatDayRange(
                    {
                      start: value?.startDate,
                      end: value?.endDate,
                    },
                    fbt,
                    locale,
                    ""
                  )
                ) : (
                  <span className="fw-lighter checkout-box-picker-selected__placeholder">
                    {fbt("Select date")}
                  </span>
                )}
              </span>
            </div>
            <div className="checkout-box-picker-icon">
              <Icon name="fi-chevron-down" size={24} />
            </div>
          </div>
        </Dropdown.Toggle>
        <Dropdown.Menu
          className="date-picker-v2 rounded"
          popperConfig={{
            modifiers: [
              {
                name: "preventOverflow",
                options: {
                  altAxis: true,
                  padding: { top: 80 },
                },
              },
            ],
          }}
          style={{ minWidth: 350 }}
          data-testid="checkout-date-picker-calendar"
        >
          {dateRangePickerComponent}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
}
